import type { CalendarMonth, Period } from "../types";

export const ONE_DAY = 1000 * 60 * 60 * 24;

export const leftPad = (v: number) => {
  if (v.toString().length === 1) return '0' + v.toString();
  return v.toString();
}

export const formatSeconds = (s: number) => {
  const days = Math.floor(s / 60 / 60 / 24);
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'}`;

  const hours = Math.floor(s / 60 / 60);
  s -= hours * 60 * 60;
  const minutes = Math.floor(s / 60);
  s -=  minutes * 60;
  const seconds = Math.floor(s);

  if (hours) return `${hours}:${leftPad(minutes)}:${leftPad(seconds)}`;
  else return `${leftPad(minutes)}:${leftPad(seconds)}`;
}

// compact "time since" label, e.g. "now", "5m ago", "3h ago", "2d ago"
export const timeSince = (timestamp: number, now = Date.now()) => {
  const seconds = Math.max(0, (now - timestamp) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

export const convertLocal = (d: Date) => (
  new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000)
)

export const parseDate = (d: string) => d ? convertLocal(new Date(d)) : undefined;

export const getTodayUTC = () => {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export const getYesterdayUTC = () => {
  const yesterday = new Date();
  // could consider skipping yesterday (currently 00:00:00 < 07:00:00)
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  yesterday.setUTCHours(0);
  yesterday.setUTCMinutes(0);
  yesterday.setUTCSeconds(0);
  yesterday.setUTCMilliseconds(0);
  return yesterday;
}

export const getPrevMonth = (date: CalendarMonth) => {
  const isFirstMonth: boolean = date.month === 0;

  const prevMonth = isFirstMonth ? 11 : date.month - 1;
  const prevYear = isFirstMonth ? date.year - 1 : date.year;

  return {
    year: prevYear,
    month: prevMonth
  };
}

export const getNextMonth = (date: CalendarMonth) => {
  const isLastMonth: boolean = date.month === 11;

  const nextMonth = isLastMonth ? 0 : date.month + 1;
  const nextYear = isLastMonth ? date.year + 1 : date.year;
 
  return {
    year: nextYear,
    month: nextMonth,
  };
}

export const getRelativeDate = (period: Period) => {
  const day = new Date();
  switch (period) {
    case 'day':
      day.setDate(day.getDate() - 1);
      return day;
    case 'week':
      day.setDate(day.getDate() - 7);
      return day;
    default:
      return new Date(2000, 0, 1);
  }
}

export const getTimeTilNextPuzzle = (lastPuzzleDate: string) => {

  const last = new Date(lastPuzzleDate ?? 0);
  const now = new Date();
  // if last puzzle is older than 1 day
  if (+now - +last > 1000 * 60 * 60 * 24) {
    return 0;
  }
 // get nearest midnight
  const tomorrow = new Date(now);

  tomorrow.setUTCDate(now.getUTCDate() + 1);
  tomorrow.setUTCHours(0, 0, 0, 0);

  const delta = tomorrow.getTime() - now.getTime();
  return delta;
}

export const dateToString = (date: Date) => (
  date.toISOString().split('T')[0]
);

export const prettyDate = (date: string) => (
  parseDate(date.split('T')[0] + 'T00:00:00Z')?.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
);

export const shortDate = (date: string) => (
  parseDate(date.split('T')[0] + 'T00:00:00Z')?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
);

export const tileDate = (date: string) => (
  parseDate(date.split('T')[0] + 'T00:00:00Z')?.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
);
